import BoxBody from 'components/BoxBody';
import { ButtonLevel, ButtonSize } from 'components/ButtonDeprecated';
import Dialog from 'components/Dialog';
import Feature from 'components/Feature';
import Glyph, { GlyphSize } from 'components/Glyph';
import Menu from 'components/Menu';
import MenuButton from 'components/MenuButtonDeprecated';
import MenuDivider from 'components/MenuDivider';
import MenuItem from 'components/MenuItem';
import ModalMenuItem from 'components/ModalMenuItem';
import TextWithIcon from 'components/TextWithIcon';
import Group from 'entities/Group';
import AddGroupOwnerModalConfirm from 'groups/components/AddGroupOwnerModalConfirm';
import { PotentialRole } from 'groups/components/OrgRoleDropdown';
import RemoveMemberMenuItem from 'groups/components/RemoveMemberMenuItem';
import invariant from 'invariant';
import RemoveFromOrgModal from 'organizations/components/RemoveFromOrgModal';
import ReportOrgUserModal from 'organizations/components/ReportOrgUserModal';
// @ts-expect-error ts-migrate(2305) FIXME: Module '"queries/__generated__/globalGQLTypes"' ha... Remove this comment to see the full error message
import { OrganizationRole } from 'queries/__generated__/globalGQLTypes';
import * as React from 'react';
import { NewUserProps as RelationshipModalUser } from 'src/user/components/RelationshipViewHeader';
import EditNameForm from 'user/components/EditNameForm';
import GetTranscriptMenuItem, { UserData } from 'user/components/GetTranscriptMenuItem';
import RelationshipModal from 'user/components/RelationshipModal';
import ReportUserMenuItem from 'user/components/ReportUserMenuItem';
import SubscribeUserMenuItem from 'user/components/SubscribeUserMenuItem';
import PreferredLanguageModal from 'user/components/PreferredLanguageModal';
import { BatchRoleCorrectionModalCommon } from 'users/components/BatchRoleCorrectionModal';
import BlockMemberMenuItem from 'users/components/BlockMemberMenuItem';
import BulkRoleChangeModal from 'users/components/BulkRoleChangeModal';
import CurrentUser from 'auth/CurrentUser';

export type UserActionsMenuButtonUser = {
  uuid: string;
  blocked: boolean;
  can_have_role_changed: boolean;
  roles: OrganizationRole[];
  potential_roles: PotentialRole[];
  isNameChangeBanned?: boolean;
} & UserData &
  RelationshipModalUser;

type Props = {
  buttonSize?: ButtonSize;
  className?: string;
  copyToClassText?: string;
  disableBlockMember?: boolean;
  disableChangeRole?: boolean;
  disableCopyToClass?: boolean;
  disabled?: boolean;
  disableGetTranscript?: boolean;
  disableMakeOwner?: boolean;
  disableRemoveFromSchool?: boolean;
  disableRemoveMember?: boolean;
  disableReportAsAdmin?: boolean;
  disableReportAsUser?: boolean;
  disableViewProfile?: boolean;
  glyphSize?: GlyphSize;
  groupToAdmin?: Group;
  organization?: {
    id: number;
    uuid: string;
    name: string;
  };
  handleClick?: Function;
  isAdminView?: boolean;
  isPrivateNoteVisible?: boolean;
  level?: ButtonLevel;
  onRevealPrivateNote?: () => void;
  onUserRemoved?: () => any;
  onUserUpdate?: (eventName?: string) => Promise<void>;
  onGetTranscript?: (requested: boolean) => void;
  showBlockMember?: boolean;
  showChangeRole?: boolean;
  showCopyToClass?: boolean;
  showGetTranscript?: boolean;
  showMakeOwner?: boolean;
  showAddPrivateNote?: boolean;
  showRemoveFromSchool?: boolean;
  showRemoveMember?: boolean;
  showReportAsAdmin?: boolean;
  showReportAsUser?: boolean;
  showViewProfile?: boolean;
  user: UserActionsMenuButtonUser;
  tooltip?: string;
  tooltipPlacement?: 'top' | 'right' | 'bottom' | 'left';
  canBanNameChanges?: boolean;
  canChangeName?: boolean;
  canUpdatePreferredLanguage?: boolean;
  preferredLanguage?: string;
  refetchContainer?: () => void;
};

// todo: Remove nested buttons
class UserActionsMenuButton extends React.Component<Props> {
  static defaultProps = {
    buttonSize: 'xsmall',
    glyphSize: 'xlarge',
    level: 'secondary',
  };

  handleUserRemoved = () => {
    const { onUserRemoved } = this.props;

    if (onUserRemoved) {
      onUserRemoved();
    }
  };

  renderMakeOwnerDialog = () => {
    const { groupToAdmin, user, refetchContainer } = this.props;
    invariant(groupToAdmin, 'renderMakeOwnerDialog called without a group');
    invariant(user, 'renderMakeOwnerDialog called without a user');
    return (
      <AddGroupOwnerModalConfirm
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        group={groupToAdmin}
        checkedIds={[user.id]}
        userName={formatNameForActionsMenuButton(user)}
        onFinish={refetchContainer}
      />
    );
  };

  renderChangeRoleModal = () => {
    const { user } = this.props;
    invariant(user, 'renderChangeRoleModal called without a user');

    const userData = [
      {
        user,
        orgAffiliationView: {
          can_have_role_changed: user.can_have_role_changed,
          roles: user.roles,
          potential_roles: user.potential_roles,
        },
      },
    ];

    if (CurrentUser.getUser().hasFeature('people_list_bulk_actions.web.jan-22')) {
      return (
        <BulkRoleChangeModal
          rows={[
            {
              user: { uuid: user.uuid, firstName: user.first_name, lastName: user.last_name },
              roles: user.roles,
            },
          ]}
          organizationUuid={this.orgFromProps(this.props, 'renderChangeRoleModal').uuid}
          onRequestHide={() => {}}
        />
      );
    } else {
      return (
        <BatchRoleCorrectionModalCommon
          organizationId={parseInt(this.orgFromProps(this.props, 'renderChangeRoleModal').id, 10)}
          userData={userData}
          // @ts-expect-error
          onUserRoleSave={this.props.onUserUpdate}
        />
      );
    }
  };

  // FIXME: There's currently two ways to pass an org into this component
  //
  // Legacy: pass a groupToAdmin with an organization in it
  // New: pass an organization separately
  //
  // The problem with switching over to the new one entirely, is RelationshipModal
  //   implicitly relies on groupToAdmin having an organization, since eventually
  //   down the line it creates a UserActionsMenuButton, which used to rely on the
  //   org in groupToAdmin for various actions.
  // The solution would be making the RelationshipModal tree pass both an organization
  //   and a group to its UserActionsMenuButton, then change all users of
  //   UserActionsMenuButton to pass organizations seperately.
  orgFromProps(props: Props, fieldName: string) {
    const { organization, groupToAdmin } = props;

    // If we have an explicit organization, just use that
    if (organization) {
      return organization;
    }

    // Otherwise try and grab the one in the groupToAdmin (legacy)
    invariant(groupToAdmin, `${fieldName} called without a group`);
    const org = groupToAdmin?.getOrganization();
    invariant(org, `${fieldName} called without a group associated to an org`);
    return org?.toJSON();
  }

  renderEditNames = () => {
    const {
      canBanNameChanges,
      canChangeName,
      user,
      onUserUpdate = async () => {},
      organization,
      groupToAdmin,
    } = this.props;

    if (!canBanNameChanges) {
      return <span />;
    }

    const modal = (
      <Dialog title="Edit name" size="small">
        <BoxBody divided size="small">
          <EditNameForm
            member={{
              uuid: user.uuid,
              firstName: user.first_name,
              lastName: user.last_name,
              userInteractionPermissions: {
                canBanNameChanges,
                canChangeName,
              },
              isNameChangeBanned: user.isNameChangeBanned,
            }}
            organizationUuid={organization?.uuid}
            groupUuid={groupToAdmin?.getUuid()}
            refetch={onUserUpdate}
          />
        </BoxBody>
      </Dialog>
    );

    return (
      <ModalMenuItem modal={modal} disabled={!canBanNameChanges} menuKey="edit-name">
        <TextWithIcon
          icon="contact-card"
          color="graphite"
          iconColor="graphite"
          iconSize="medium"
          size="xsmall"
        >
          Edit name
        </TextWithIcon>
      </ModalMenuItem>
    );
  };

  renderMenu = () => {
    const {
      canUpdatePreferredLanguage,
      copyToClassText,
      disableBlockMember,
      disableChangeRole,
      disableCopyToClass,
      disableGetTranscript,
      disableMakeOwner,
      disableRemoveFromSchool,
      disableRemoveMember,
      disableReportAsAdmin,
      disableReportAsUser,
      disableViewProfile,
      groupToAdmin,
      organization,
      isAdminView,
      isPrivateNoteVisible,
      onUserRemoved,
      onUserUpdate,
      onGetTranscript,
      onRevealPrivateNote,
      preferredLanguage,
      showAddPrivateNote,
      showBlockMember,
      showChangeRole,
      showCopyToClass,
      showGetTranscript,
      showMakeOwner,
      showRemoveFromSchool,
      showRemoveMember,
      showReportAsAdmin,
      showReportAsUser,
      showViewProfile,
      user,
    } = this.props;

    const hasItemsAboveFirstDivider = showChangeRole || showGetTranscript || showCopyToClass;
    const hasItemsBelowFirstDivider =
      showRemoveMember || showBlockMember || showReportAsUser || showReportAsAdmin;

    return (
      <Menu rowHover>
        <Feature name="profile_refresh.web.5-5">{this.renderEditNames()}</Feature>
        <PreferredLanguageModal
          menuKey="edit-preferred-language"
          userUuid={user.uuid}
          groupToAdmin={groupToAdmin}
          organization={organization}
          onUserUpdate={onUserUpdate}
          canUpdatePreferredLanguage={canUpdatePreferredLanguage}
          preferredLanguage={preferredLanguage}
        />
        {showAddPrivateNote && (
          <Feature name="profile_refresh.web.5-5">
            <MenuItem onClick={onRevealPrivateNote} menuKey="showPrivateNote">
              <Glyph name="note" />
              <span>{isPrivateNoteVisible ? 'Edit' : 'Add'} private note</span>
            </MenuItem>
          </Feature>
        )}

        {showViewProfile && (
          <ModalMenuItem
            eventElementName="user_action_menu_view_profile"
            modal={this.renderRelationshipDialog()}
            disabled={disableViewProfile}
          >
            <Glyph name="avatar" />
            <span>View profile</span>
          </ModalMenuItem>
        )}
        {showChangeRole && (
          <ModalMenuItem
            eventElementName="user_action_menu_change_role"
            modal={this.renderChangeRoleModal()}
            disabled={disableChangeRole}
          >
            <Glyph name="pencil-stroke" />
            <span>Change role</span>
          </ModalMenuItem>
        )}
        {showCopyToClass && (
          <SubscribeUserMenuItem
            orgUuid={organization?.uuid}
            subscriberId={parseInt(user.id, 10)}
            disabled={!!disableCopyToClass}
            eventElementName="user_action_menu_copy_to_class"
          >
            {copyToClassText}
          </SubscribeUserMenuItem>
        )}
        {showMakeOwner && (
          <ModalMenuItem
            eventElementName="user_action_menu_make_owner"
            modal={this.renderMakeOwnerDialog()}
            disabled={disableMakeOwner}
          >
            <Glyph name="crown-stroke" />
            <span>Make owner of class</span>
          </ModalMenuItem>
        )}
        {hasItemsAboveFirstDivider && hasItemsBelowFirstDivider && <MenuDivider />}

        {showGetTranscript && (organization || groupToAdmin) && (
          <GetTranscriptMenuItem
            disabled={disableGetTranscript}
            user={user}
            organization={this.orgFromProps(this.props, 'GetTranscriptMenuItem')}
            isAdminView={isAdminView}
            onGetTranscript={onGetTranscript}
          />
        )}

        {showRemoveMember && groupToAdmin && (
          <RemoveMemberMenuItem
            dataTestSelector="remove-member-menu-item"
            eventElementName="user_action_menu_remove_member"
            disabled={disableRemoveMember}
            group={groupToAdmin}
            groupName={groupToAdmin.getName()}
            groupUuid={groupToAdmin.getUuid()}
            member={user}
            memberName={user.name}
            memberUuid={user.uuid}
            onRemove={onUserRemoved}
          />
        )}
        {showBlockMember && (
          <BlockMemberMenuItem
            disabled={disableBlockMember}
            blocked={user.blocked}
            memberId={parseInt(user.id, 10)}
            memberName={formatNameForActionsMenuButton(user)}
          />
        )}
        {showReportAsUser && (
          <ReportUserMenuItem
            reportee={user}
            disabled={!!disableReportAsUser}
            // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
            eventElementName="user_action_menu_report_as_user"
          />
        )}
        {showReportAsAdmin && (
          <ModalMenuItem
            disabled={disableReportAsAdmin}
            eventElementName="user_action_menu_report_as_admin"
            modal={this.renderReportOrgUserModal()}
          >
            <Glyph name="flag" />
            <span>Report to Remind</span>
          </ModalMenuItem>
        )}
        {showRemoveFromSchool && (organization || groupToAdmin) && (
          <React.Fragment>
            <MenuDivider />
            <ModalMenuItem
              eventElementName="user_action_menu_remove_from_school"
              modal={this.renderRemoveFromOrgModal()}
              disabled={disableRemoveFromSchool}
            >
              <Glyph name="close" />
              <span>Remove from school</span>
            </ModalMenuItem>
          </React.Fragment>
        )}
      </Menu>
    );
  };

  renderRelationshipDialog = () => {
    const { groupToAdmin, user } = this.props;
    invariant(user, 'renderRelationshipDialog called without a user');

    // @ts-expect-error ts-migrate(2741) FIXME: Property 'onRequestHide' is missing in type '{ gro... Remove this comment to see the full error message
    return <RelationshipModal groupToAdmin={groupToAdmin} userId={user.id} user={user} />;
  };

  renderRemoveFromOrgModal = () => {
    const { user, isAdminView } = this.props;

    const org = this.orgFromProps(this.props, 'renderRemoveFromOrgModal');
    invariant(user, 'renderRemoveFromOrgModal called without a user');

    return (
      <RemoveFromOrgModal
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        isAdminView={isAdminView}
        userName={formatNameForActionsMenuButton(user)}
        userIds={[user.id]}
        orgName={org.name}
        orgId={org.id}
        reportModal={this.renderReportOrgUserModal}
        onRemoved={this.handleUserRemoved}
      />
    );
  };

  renderReportOrgUserModal = () => {
    const { user } = this.props;

    const org = this.orgFromProps(this.props, 'renderReportOrgUserModal');
    invariant(user, 'renderReportOrgUserModal called without a user');

    return (
      <ReportOrgUserModal
        userName={formatNameForActionsMenuButton(user)}
        userId={user.id}
        orgName={org.name}
        orgId={org.id}
        removeModal={this.renderRemoveFromOrgModal}
      />
    );
  };

  handleClick = (event: any) => {
    event.stopPropagation();

    if (this.props.handleClick) {
      this.props.handleClick(event);
    }
  };

  render() {
    const { buttonSize, className, disabled, glyphSize, level, tooltip, tooltipPlacement } =
      this.props;

    return (
      <MenuButton
        alignment="right"
        disabled={disabled}
        level={level}
        menu={this.renderMenu()}
        size={buttonSize}
        tooltip={tooltip}
        tooltipPlacement={tooltipPlacement}
        className={className}
        data-test-selector="user-actions-menu-button"
        data-testid="user-actions-menu-button"
        onClick={this.handleClick}
      >
        <Glyph name="ellipses" size={glyphSize} solo />
      </MenuButton>
    );
  }
}

export function formatNameForActionsMenuButton(user: any): string {
  const firstName = user.first_name || user.firstName;
  const lastName = user.last_name || user.lastName;
  let name = '';
  if (firstName && lastName) {
    name = `${firstName} ${lastName}`;
  } else if (user.name) {
    name = user.name;
  } else if (firstName) {
    name = firstName;
  }
  return name;
}

export default UserActionsMenuButton;
